import CrudApiClient from './crudapiclient';
import { GATEWAY_API_URL } from '@admin-panel/config/env';
import { CrudFilters, CrudSort, MetaQuery } from '@refinedev/core';
import { axiosInstance } from './axios';
import {
  IReturnRequest,
  ReturnCourierExceptionResolutionStatus,
  ReturnStatus,
} from '@admin-panel/types';
import { putGeofenceException, type GeofenceExceptionDecision } from './geofenceexception';

class ReturnAPIClient extends CrudApiClient {
  constructor() {
    super(GATEWAY_API_URL + '/order/return');
  }

  /** Hub location exception for the return's current rider leg (pickup, or the drop once picked up). */
  async updateGeofenceException(
    returnId: number,
    payload: GeofenceExceptionDecision,
  ): Promise<IReturnRequest> {
    const data = await putGeofenceException(this.baseURL ?? '', returnId, payload);
    return this.transformNode(data as unknown as IReturnRequest);
  }

  async performAction(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    try {
      let url = this.baseURL + '/' + meta?.returnId + '/transition';
      if (payload.status == ReturnStatus.CANCELLED) {
        url = this.baseURL + '/' + meta?.returnId + '/cancel';
      }

      console.log('Calling return action api', url);

      const response = await axiosInstance.put(`${url}`, payload);
      if (response.data?.success === false || response.data?.status?.type === 'ERROR') {
        throw new Error(response.data?.status?.message || 'Unable to update the return');
      }
      return this.transformNode(response.data?.data);
    } catch (error) {
      throw error;
    }
  }

  async findPorterCancelReview(
    _sorters: CrudSort[],
    _filters: CrudFilters,
    _payload: any,
    _query: any,
    _headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const response = await axiosInstance.get(`${this.baseURL}/${meta?.['returnId']}/porter/cancel`);
    return response.data?.data;
  }

  async cancelPorter(
    _sorters: CrudSort[],
    _filters: CrudFilters,
    payload: any,
    _query: any,
    _headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const response = await axiosInstance.put(
      `${this.baseURL}/${meta?.['returnId']}/porter/cancel`,
      payload,
    );
    return response.data?.data;
  }

  async findPickupChange(
    _sorters: CrudSort[],
    _filters: CrudFilters,
    _payload: any,
    _query: any,
    _headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const response = await axiosInstance.get(`${this.baseURL}/${meta?.['returnId']}/pickup-change`);
    return response.data?.data;
  }

  async changePickup(
    _sorters: CrudSort[],
    _filters: CrudFilters,
    payload: any,
    _query: any,
    _headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const response = await axiosInstance.put(
      `${this.baseURL}/${meta?.['returnId']}/pickup-change`,
      payload,
    );
    return response.data?.data;
  }

  async checkPickupChange(
    _sorters: CrudSort[],
    _filters: CrudFilters,
    _payload: any,
    _query: any,
    _headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    if (!meta?.['operationId'])
      throw new Error('Refresh the saved pickup change before checking it.');
    const response = await axiosInstance.put(
      `${this.baseURL}/${meta?.['returnId']}/pickup-change/${encodeURIComponent(String(meta['operationId']))}/check`,
      {},
    );
    return response.data?.data;
  }

  async syncSameBookingAwb(
    _sorters: CrudSort[],
    _filters: CrudFilters,
    payload: any,
    _query: any,
    _headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const response = await axiosInstance.put(
      `${this.baseURL}/${meta?.['returnId']}/shiprocket-pickup/sync-awb`,
      payload,
    );
    return response.data?.data;
  }

  async regenerateShiprocketPickup(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const response = await axiosInstance.put(
      `${this.baseURL}/${meta?.returnId}/shiprocket-pickup/regenerate`,
      payload,
    );
    return response.data?.data;
  }

  async replaceShiprocketPickup(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const response = await axiosInstance.put(
      `${this.baseURL}/${meta?.returnId}/shiprocket-pickup/replace`,
      payload,
    );
    return response.data?.data;
  }

  async findPickupCoordinationOptions(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const response = await axiosInstance.get(
      `${this.baseURL}/${meta?.returnId}/pickup-coordination/options`,
    );
    if (response.data?.success === false || response.data?.status?.type === 'ERROR') {
      throw new Error(
        response.data?.status?.message || 'Unable to check other open return pickups.',
      );
    }
    return response.data?.data || [];
  }

  async findPorterReturnQuote(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const response = await axiosInstance.get(`${this.baseURL}/${meta?.returnId}/porter/quote`, {
      params: {
        usePinLocation: meta?.usePinLocation === true,
        ...(meta?.['forPickupChange'] === true ? { forPickupChange: true } : {}),
      },
    });
    if (response.data?.success === false || response.data?.status?.type === 'ERROR') {
      throw new Error(response.data?.status?.message || 'Unable to get a Porter quote.');
    }
    return response.data?.data;
  }

  async useExistingPickup(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const response = await axiosInstance.put(
      `${this.baseURL}/${meta?.returnId}/pickup-coordination`,
      payload,
    );
    if (response.data?.success === false || response.data?.status?.type === 'ERROR') {
      throw new Error(response.data?.status?.message || 'Unable to use the existing pickup.');
    }
    return this.transformNode(response.data?.data);
  }

  async findCourierEvidence(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const response = await axiosInstance.get(`${this.baseURL}/${meta?.returnId}/courier-evidence`);
    return response.data?.data;
  }

  async listCancelledAwbMovementExceptions(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const page = Number(meta?.page || meta?.currentPage || 1);
    const size = Number(meta?.pageSize || 50);
    const resolutionStatus = meta?.resolutionStatus || ReturnCourierExceptionResolutionStatus.OPEN;
    const params = new URLSearchParams({
      resolutionStatus: String(resolutionStatus),
      page: String(page),
      size: String(size),
    });
    const response = await axiosInstance.get(
      `${this.baseURL}/exceptions/cancelled-awb-movement?${params.toString()}`,
    );
    return {
      data: response.data?.data || [],
      total: response.data?.status?.count || 0,
    };
  }

  async findCancelledAwbMovementExceptionsForReturn(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const response = await axiosInstance.get(
      `${this.baseURL}/${meta?.returnId}/exceptions/cancelled-awb-movement`,
    );
    return response.data?.data || [];
  }

  async migrateCancelledAwbTracking(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const response = await axiosInstance.put(
      `${this.baseURL}/${meta?.returnId}/migrate-cancelled-awb`,
      payload,
    );
    return this.transformNode(response.data?.data);
  }

  async closeCancelledAwbMovementException(
    sorters: CrudSort[],
    filters: CrudFilters,
    payload: any,
    query: any,
    headers: any,
    meta: MetaQuery | undefined,
  ): Promise<any> {
    const response = await axiosInstance.put(
      `${this.baseURL}/exceptions/cancelled-awb-movement/${meta?.exceptionId}/close`,
      payload,
    );
    return this.transformNode(response.data?.data);
  }
}

export default ReturnAPIClient;
